/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { BaseComponent } from '../../../../../core/components';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../../../../core/models';
import { FormGroup } from '@angular/forms';
import { LogService, NavigationService, UtilsService } from '../../../../../core/services';
import { MetricsEnum, SystemApiService } from '../../../shared';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-overview-details',
    templateUrl: './overview-details.component.html',
    styleUrls: ['./overview-details.component.scss']
})
export class OverviewDetailsComponent extends BaseComponent {
    /**
     * Expose the button element to parent components
     */
    @ViewChild('canvas', { read: ElementRef }) public canvas: any;

    /**
     * Store the type and periods for the graphs
     */
    public dropdown: any = {
        types: [],
        periods: [],
    };

    /**
     * The main formGroup variable.
     */
    public formGroup: FormGroup;

    /**
     * Store the selected metric for making API calls.
     */
    public selectedType: string;

    /**
     *  Store the selected interval for making API calls.
     */
    public selectedPeriod = 'day';

    /**
     * Graph datasets.
     */
    public data = {
        datasets: []
    };

    /**
     * Graph options.
     * DOCS - https://www.chartjs.org/docs/latest/
     */
    public graphOptions: any = {
        responsive: true,
        animation: { duration: 1 },
        elements: {
            point: { radius: 0 },
            line: {
                spanGaps: false
            }
        },
        scales: {
            xAxes: {
                ticks: {
                    autoSkip: true,
                }
            },
            yAxes: { min: 0, beginAtZero: false }
        },
        tooltips: {
            mode: 'nearest',
            intersect: false
        },
        hover: {
            mode: 'nearest',
            intersect: false
        }
    };

    /**
     * Store loading state.
     */
    public loading: boolean = false;

    /**
     * Title
     */
    public title: string;

    /**
     * Set metric scale for widget.
     */
    public scale: string;

    /**
     * Store a range of colours for the graphs to use.
     * @private
     */
    private colors: string[] = [
        '#00b391',
        '#f41171',
        '#5c61d4',
        '#ffa340',
        '#333',
        '#c200ff',
        '#faee00',
        '#54009c',
    ];

    constructor(
        private activatedRoute: ActivatedRoute,
        private logService: LogService,
        private navigationService: NavigationService,
        private router: Router,
        private systemApiService: SystemApiService,
        private utilsService: UtilsService
    ) {
        super();

        this.subscribeToRouteChanges();
        this.setUpView();
    }

    /**
     * Update the selected metric type.
     * @param type
     */
    public updateSelectedType(type: any): void {
        const selectedType: string = this.getSelectedType(type.key);
        const parts: string[] = this.router.url.split('/');
        if (!this.loading && parts[parts.length - 2] !== selectedType) {
            this.navigateToNewRoute(`${selectedType}/${this.selectedPeriod}`);
        }
    }

    /**
     * Update the selected period.
     * @param period
     */
    public updateSelectedPeriod(period: any): void {
        const parts: string[] = this.router.url.split('/');
        if (!this.loading && parts[parts.length - 1] !== period.key) {
            this.navigateToNewRoute(`${this.selectedType}/${period.key}`);
        }
    }

    /**
     * Navigate to new route.
     * @param params
     */
    public navigateToNewRoute(params: string) {
        this.loading = true;
        const link = `/overview/details/${params}`;
        this.router.navigateByUrl(this.navigationService.getEnduranceUrl(link)).then().catch();
    }

    /**
     * Set graph data.
     */
    public setGraphData(refreshData?: boolean): void {
        if (refreshData) {
            this.loading = true;
        }

        this.utilsService.debounce('apiCall', () => { // Setting dropdowns causes the API call to be made multiple times onload.
            const params = `?period=${this.selectedPeriod}&type=${this.selectedType}`;
            this.systemApiService.getSystemStats(params)
                .pipe(takeUntil(this.destroyed$))
                .subscribe({
                    next: (data: any): void => {
                        this.dataSwitchStatement(data);
                        this.loading = false;
                    },
                    error: (error: ErrorResponseModel): void => {
                        this.logService.error('OverviewDetailsComponent:setGraphData', error.message);
                        this.data.datasets = [];
                    }
                });
        }, 500);
    }

    /**
     * Set selected metric.
     * @param type
     * @private
     */
    private getSelectedType(type: string): string {
        switch (type) {
            case 'network-graph':
            case 'interfaces':
                return 'interfaces';
            case 'cpu':
            case 'system-load-average':
                return 'cpu';
            case 'memory':
            case 'system-memory-usage':
                return 'memory';
            case 'disks':
                return 'disks';
            default:
                return type;
        }
    }

    /**
     * Subscribe to route changes and close emitter on navigation.
     * @private
     */
    private subscribeToRouteChanges(): void {
        this.router.events.subscribe({
            next: (event: any): void => {
                if (event instanceof NavigationEnd) {
                    this.setUpView();
                }
            }
        });
    }

    /**
     * Set up the view.
     * @private
     */
    private setUpView(): void {
        this.selectedType = this.getSelectedType(this.activatedRoute.snapshot.params['graphType']);
        this.selectedPeriod = this.activatedRoute.snapshot.params['period'];
        this.setUpDropdowns();
        this.setGraphData();
    }

    /**
     * Set up dropdowns
     * @private
     */
    private setUpDropdowns(): void {
        this.dropdown.types = [
            { key: 'cpu', value: 'CPU' },
            { key: 'memory', value: 'Memory' },
            { key: 'disks', value: 'Disks' },
            { key: 'interfaces', value: 'Interfaces' }
        ];

        this.dropdown.periods = [
            { key: 'hour', value: '1 Hour' },
            { key: 'day', value: '1 Day' },
            { key: 'week', value: '1 Week' },
            { key: 'month', value: '1 Month' },
            { key: 'year', value: '1 Year' }
        ];
    }

    /**
     * Switch statement for each widget usage.
     * @private
     * @param data
     */
    private dataSwitchStatement(data: any): void {
        switch (this.selectedType) {
            case MetricsEnum.NETWORKGRAPH:
            case MetricsEnum.INTERFACES:
                this.title = MetricsEnum.NETWORKGRAPH;
                this.scale = 'kbs';
                this.setNetworkGraphData(data.interfaces);
                break;
            case MetricsEnum.CPU:
            case MetricsEnum.LOADAVERAGEGRAPH:
                this.title = MetricsEnum.LOADAVERAGEGRAPH;
                this.scale = '%';
                this.setCpuGraphData(data.cpu);
                break;
            case MetricsEnum.MEMORY:
            case MetricsEnum.MEMORYGRAPH:
                this.title = MetricsEnum.MEMORYGRAPH;
                this.scale = 'Mb';
                this.setMemoryGraphData(data.memory);
                break;
            case MetricsEnum.DISKS:
                this.title = MetricsEnum.DISKS;
                this.scale = '%';
                this.setDiskGraphData(data.disks);
                break;
        }
    }

    /**
     * Set the network graph data.
     * @param interfaces
     * @private
     */
    private setNetworkGraphData(interfaces: any): void {
        this.data.datasets = [
            {
                label: 'rx',
                lineTension: 0,
                data: [{ x: 0, y: 0 }]
            },
            {
                label: 'tx',
                lineTension: 0,
                data: [{ x: 0, y: 0 }]
            },
        ];

        const initTenMinutes: any[] = [];
        let isInitData: boolean = false;

        // Loop through all interfaces to get each set
        for (const interfaceName in interfaces) {
            if (interfaces.hasOwnProperty(interfaceName)) {
                const iFace = interfaces[interfaceName];

                // Loop through each interface e.g. ens33
                for (const rxTxProperties in iFace) {
                    if (iFace.hasOwnProperty(rxTxProperties)) {
                        isInitData = Object.values(iFace[rxTxProperties]).length > 1;
                        const dataGroup = iFace[rxTxProperties];
                        const lineData: any[] = [];

                        // loop through both the TX and RX values
                        for (const item in dataGroup) {
                            if (dataGroup.hasOwnProperty(item)) {
                                const key: string = item;

                                lineData.push({
                                    x: this.formatDate(key),
                                    y: dataGroup[item]
                                });
                            }
                        }

                        if (!isInitData) {
                            const index: number = this.data.datasets.findIndex(
                                dataSet => dataSet.label === `${interfaceName} - ${rxTxProperties}`
                            );

                            if (index !== -1) {
                                const data = this.data.datasets[index];
                                data.data.push(lineData[0]);
                                data.data.shift();
                                data.borderColor = this.colors[index];
                                data.backgroundColor = this.colors[index];
                            }
                        }

                        if (isInitData) {
                            initTenMinutes.push({
                                label: `${interfaceName} - ${rxTxProperties}`,
                                fill: false,
                                lineTension: 0,
                                data: lineData,
                            });
                        }
                    }
                }
            }
        }

        if (isInitData) {
            this.data.datasets = initTenMinutes;
        }

        this.data.datasets = [...this.data.datasets];
    }

    /**
     * Set CPU graph data.
     * @param cpuValues
     * @private
     */
    private setCpuGraphData(cpuValues: any): void {
        this.data.datasets = [
            {
                label: 'CPU',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#54009c',
                backgroundColor: '#54009c'
            },
        ];

        const lineData: any[] = [];
        for (const [key, value] of Object.entries(cpuValues)) {
            lineData.push({ x: this.formatDate(key), y: value as number });
        }

        this.data.datasets[0].data = lineData;
    }

    /**
     * Set memory graph data.
     * @param memory
     * @private
     */
    private setMemoryGraphData(memory: any): void {
        this.data.datasets = [
            {
                label: 'Free',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#00b391',
                backgroundColor: '#00b391',
            },
            {
                label: 'Used',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#f41171',
                backgroundColor: '#f41171',
            },
            {
                label: 'Buffered',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#5c61d4',
                backgroundColor: '#5c61d4',
            },
            {
                label: 'Cached',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#ffa340',
                backgroundColor: '#ffa340',
            },
        ];

        const memoryTypes = ['used', 'free', 'buffered', 'cached'];
        const graphData = {
            used: [],
            free: [],
            buffered: [],
            cached: []
        };

        for (const [key, value] of Object.entries(memory)) {
            memoryTypes.forEach(type => {
                graphData[type].push({
                    x: this.formatDate(key),
                    y: Math.round(value[type] / Math.pow(1024, 2))
                });
            });
        }

        memoryTypes.forEach((type, index) => {
            this.data.datasets[index].data = graphData[type];
        });
    }

    /**
     * Set disk graph data.
     * @private
     * @param response
     */
    private setDiskGraphData(response: any): any {
        this.data.datasets = [
            {
                label: 'Root',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#5c61d4',
                backgroundColor: '#5c61d4'
            },
            {
                label: 'Log',
                lineTension: 0,
                data: [{ x: 0, y: 0 }],
                borderColor: '#00b391',
                backgroundColor: '#00b391'
            }
        ];

        const diskTypes: string[] = ['root', 'log'];
        const graphData = {
            root: [],
            log: []
        };

        for (const [key, values] of Object.entries(response)) {
            // @ts-ignore
            for (const value in values) {
                if (values.hasOwnProperty(value)) {
                    graphData[key].push({
                        x: this.formatDate(value),
                        y: (values[value].used / (values[value].used + values[value].free)) * 100
                    });
                }

            }
        }

        diskTypes.forEach((type: string, index: number) => {
            this.data.datasets[index].data = graphData[type];
        });
    }

    /**
     * Format date.
     * @param timestamp
     * @private
     */
    private formatDate(timestamp: string): string {
        const dateTime = new Date(parseInt(timestamp, 10) * 1000);
        const locale: string = Intl.DateTimeFormat().resolvedOptions().locale;
        let includeDate: boolean = true;
        let optionsTime: any = { hour12: false };

        switch (this.selectedPeriod) {
            case 'hour':
                includeDate = false;
                break;
            case 'day':
                includeDate = false;
                optionsTime = { hour12: false, hour: '2-digit', minute: '2-digit' };
                break;
            case 'week':
            case 'month':
            case 'year':
                optionsTime = { hour12: false, hour: '2-digit', minute: '2-digit' };
                break;
        }

        return (includeDate ? dateTime.toLocaleDateString(locale) : '') + '  ' + dateTime.toLocaleTimeString(locale, optionsTime);
    }
}
